import type { Lesson } from './courses';

/**
 * Parcours : AI Router — 4 lecons.
 */
export const AI_ROUTER_CONFIG_LESSONS: Lesson[] = [
  {
    slug: 'multi-provider-architecture',
    title: 'Architecture multi-provider (Groq, Claude, OpenAI, Local)',
    duration: '5 min',
    glossaryTerms: ['ai-router', 'provider', 'model'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'AI Router est le composant central qui orchestre les appels vers differents fournisseurs d'IA. Feedback Orchestrator supporte un double moteur :\n\n- **Moteur open-source (illimite)** — Groq (Llama, Mixtral) et modeles locaux via Ollama. Gratuit, pas de quota, ideal pour le plan Free.\n- **Moteur premium** — Claude (Anthropic) et OpenAI (GPT-4, Realtime). Qualite superieure, utilise les tokens du plan ou les cles BYOK.\n\nLe router choisit automatiquement le meilleur provider selon la tache, le plan de l'utilisateur et la disponibilite des services.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Une architecture multi-provider offre plusieurs avantages strategiques :\n\n- **Resilience** — Si un provider tombe, le router bascule automatiquement sur un fallback\n- **Optimisation des couts** — Les taches simples utilisent le moteur gratuit, les taches complexes le premium\n- **Flexibilite** — L'utilisateur peut choisir son provider prefere via BYOK\n- **Scalabilite** — Ajout de nouveaux providers sans modification du code metier\n\nLa decision strategique : monetiser la plateforme (serveurs, sieges) et non l'IA elle-meme, grace au moteur open-source illimite.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Comprendre le routage** — Chaque type de tache (analyse, generation, traduction) a un provider par defaut\n2. **Configurer les priorites** — Le router suit l'ordre : BYOK utilisateur > provider premium plan > open-source\n3. **Verifier la disponibilite** — Le health check tourne toutes les 60 secondes\n4. **Monitorer l'usage** — Le dashboard tech affiche les tokens consommes par provider\n5. **Ajuster si necessaire** — Modifier les priorites de routage dans la configuration admin",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "L'onglet **AI Router** dans les pages Tech (ROLE_ADMIN uniquement) affiche l'etat de sante de chaque provider, les statistiques d'utilisation et les configurations de routage. Le monitoring inclut la latence, le taux d'erreur et la consommation de tokens.",
      },
    ],
    linkedModule: {
      label: 'Configuration Tech',
      path: '/tech/ai-router',
      icon: 'Zap',
    },
  },
  {
    slug: 'configure-providers',
    title: 'Configurer les providers par tache',
    duration: '4 min',
    glossaryTerms: ['task-routing', 'priority', 'fallback'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Chaque type de tache IA est associe a une chaine de providers prioritaires. Les taches principales :\n\n- **Analyse de feedback** — Extraction de sentiments, tags, priorites → Groq (rapide) ou Claude (precis)\n- **Generation de contenu** — Articles blog, emails, battlecards → Claude ou GPT-4 (creativite)\n- **Traduction** — FR ↔ EN, adaptation culturelle → Claude (meilleur en multilingue)\n- **Profilage prospect** — Analyse LinkedIn, detection persona → GPT-4 ou Claude\n- **Conversation temps reel** — Assistant vocal / chat → OpenAI Realtime ou Claude\n\nLe routage est configurable par l'administrateur pour chaque type de tache.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Chaque provider a des forces differentes :\n\n- **Groq** — Latence tres faible (~100ms), ideal pour l'analyse en temps reel\n- **Claude** — Meilleur raisonnement et contexte long, ideal pour la generation\n- **GPT-4** — Polyvalent, bon en creativite et instruction following\n- **Local (Ollama)** — Zero cout, zero latence reseau, mais qualite variable\n\nConfigurer le bon provider par tache optimise a la fois la qualite et le cout.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Lister les taches IA** — Identifier toutes les fonctionnalites qui utilisent l'IA\n2. **Evaluer les besoins** — Latence, qualite, cout pour chaque tache\n3. **Definir les priorites** — Provider principal + fallback(s) pour chaque tache\n4. **Tester** — Executer chaque tache avec les differents providers et comparer\n5. **Deployer** — Appliquer la configuration via l'interface admin",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La page de configuration du routage IA permet de definir pour chaque type de tache le provider principal et les fallbacks. Un mode 'test' permet de comparer les resultats de differents providers sur un meme input avant de valider la configuration.",
      },
    ],
    linkedModule: {
      label: 'Configuration Tech',
      path: '/tech/ai-router',
      icon: 'Zap',
    },
  },
  {
    slug: 'byok',
    title: 'BYOK (Bring Your Own Key)',
    duration: '3 min',
    glossaryTerms: ['byok', 'api-key', 'encryption'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "BYOK (Bring Your Own Key) permet aux utilisateurs de connecter leurs propres cles API de fournisseurs d'IA. Cela debloque plusieurs avantages :\n\n- **Plan Free illimite** — L'utilisateur paie directement son provider, pas de quota FO\n- **Controle des couts** — L'utilisateur maitrise sa consommation directement\n- **Choix du provider** — L'utilisateur peut preferer un provider specifique\n- **Conformite** — Les donnees transitent via le compte du client, pas celui de FO\n\nLes cles sont chiffrees en AES-256 cote serveur et jamais exposees dans les reponses API.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "BYOK est au coeur de la strategie de monetisation de Feedback Orchestrator :\n\n- **Acquisition** — Le plan Free + BYOK elimine la friction financiere a l'entree\n- **Confiance** — Le client garde le controle de ses cles et de ses couts\n- **Scalabilite** — Pas de cout IA variable pour FO sur les utilisateurs Free\n- **Monetisation** — La valeur payante est la plateforme (serveurs, collaboration, features), pas l'IA\n\nC'est une decision strategique validee : l'IA est un moyen, pas le produit.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Acceder aux parametres** — Dans les Settings de l'organisation\n2. **Ajouter une cle** — Selectionner le provider (OpenAI, Claude, Groq) et coller la cle API\n3. **Valider** — Le systeme teste la cle avec un appel minimal pour verifier sa validite\n4. **Activer** — La cle BYOK devient prioritaire sur le provider de la plateforme\n5. **Monitorer** — L'usage reste visible dans le dashboard, meme avec une cle BYOK",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Les cles BYOK se configurent dans **Settings > IA & Providers**. Un indicateur visuel montre quelles cles sont actives et le provider utilise pour chaque tache. Le systeme alerte si une cle expire ou atteint ses limites.",
      },
    ],
    linkedModule: {
      label: 'Parametres',
      path: '/settings',
      icon: 'Settings',
    },
  },
  {
    slug: 'monitoring-fallback',
    title: 'Monitoring et fallback',
    duration: '4 min',
    glossaryTerms: ['monitoring', 'fallback', 'health-check'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le monitoring de l'AI Router couvre trois dimensions :\n\n**Health Checks** :\n- Verification periodique (60s) de la disponibilite de chaque provider\n- Mesure de la latence et du taux d'erreur\n- Detection automatique des degradations de service\n\n**Metriques** :\n- Tokens consommes par provider, par tache, par organisation\n- Latence moyenne et P95 par provider\n- Taux de succes / erreur par provider\n\n**Fallback** :\n- Si le provider principal echoue, basculement automatique sur le suivant dans la chaine\n- Retry avec backoff exponentiel (1s, 2s, 4s)\n- Circuit breaker : apres 5 echecs consecutifs, le provider est desactive temporairement",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les API d'IA sont des services externes dont la disponibilite n'est pas garantie a 100%. Un fallback bien configure :\n\n- Garantit une disponibilite quasi-100% pour les utilisateurs\n- Evite les erreurs silencieuses qui degradent l'experience\n- Permet de detecter les problemes avant qu'ils n'impactent les clients\n\nExemple concret : quand OpenAI a eu une panne de 4h en janvier 2026, les systemes avec fallback vers Claude n'ont pas ete impactes.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Verifier le dashboard** — Le statut de chaque provider est visible en temps reel\n2. **Configurer les alertes** — Notifications si un provider depasse le seuil d'erreur\n3. **Definir les fallbacks** — Pour chaque tache, au moins 2 providers dans la chaine\n4. **Tester les fallbacks** — Simuler une panne d'un provider et verifier la bascule\n5. **Analyser les metriques** — Identifier les tendances (latence croissante, erreurs recurrentes)",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le dashboard **Tech > AI Router** affiche en temps reel l'etat de chaque provider (vert/jaune/rouge), les metriques d'usage et l'historique des incidents. Les alertes sont envoyees par email et webhook aux administrateurs.",
      },
    ],
    linkedModule: {
      label: 'Configuration Tech',
      path: '/tech/ai-router',
      icon: 'Zap',
    },
  },
];
